import type { FilesApi } from '@odo/shared-ui';
import api, { files_api } from '../api/config';
import { deleteFile, getDownloadUrl, uploadFile, type PatientFileType } from './cloud-files';

const fetchFileViaProxy = async (url: string, filename: string): Promise<Blob> => {
    const response = await files_api.get('/contents/file-proxy', {
        params: { url, filename },
        responseType: 'blob'
    });
    return response.data;
};

const updateCloudFile: FilesApi['updateFile'] = async (patientId, patientFileId, changes) => {
    const body: Record<string, unknown> = {};
    if (changes.originalFilename !== undefined) body.originalFilename = changes.originalFilename;
    if (changes.fileType !== undefined) body.fileType = changes.fileType;
    if (changes.metadata !== undefined) body.metadata = changes.metadata;
    await api.patch(`/patients/${patientId}/cloud-files/${patientFileId}`, body);
};

/**
 * dashboard implementation of the shared files module's IO seam, backed by the
 * app's authenticated axios client (see services/cloud-files for the
 * underlying endpoint wrappers).
 */
export const filesApi: FilesApi = {
    getDownloadUrl: (patientId, patientFileId) => getDownloadUrl(patientId, patientFileId),
    deleteFile: (patientId, patientFileId) => deleteFile(patientId, patientFileId),
    updateFile: updateCloudFile,
    uploadFile: async (patientId, file, options) => {
        try {
            const patientFile = await uploadFile(
                patientId,
                file,
                (options?.fileType as PatientFileType | undefined) ?? 'ADDITIONAL',
                'Uploaded via files_tab',
                options?.metadata,
                options?.onProgress
            );
            return { success: true, patientFileId: patientFile.id, fileId: patientFile.fileId };
        } catch (error: unknown) {
            const errorObj = error as {
                response?: { data?: { message?: string } };
                message?: string;
                stack?: string;
            };
            return {
                success: false,
                error: errorObj?.response?.data?.message || errorObj?.message || 'Upload failed',
                errorStack: errorObj?.stack
            };
        }
    },
    fetchViaProxy: (url, filename) => fetchFileViaProxy(url, filename)
};
